import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import api from '@/lib/api';


const CampaignDetailsModal = ({ isOpen, onClose, campaignId, onEdit, onDelete, scenarios = [] }) => {
    const [campaign, setCampaign] = useState(null);

    useEffect(() => {
        if (!isOpen || !campaignId) return;

        let canceled = false;
        const fetchDetails = async () => {
            try {
                const res = await api.get(`/api/campaigns/${campaignId}/`);
                if (!canceled) setCampaign(res.data);
            } catch (e) {
                console.error('❌ Ошибка загрузки кампании:', e);
            }
        };

        fetchDetails();

        // если не активна — не поллим
        const shouldPoll = !campaign || campaign.status === 'active';
        const id = shouldPoll ? setInterval(fetchDetails, 10000) : null;

        return () => {
            canceled = true;
            if (id) clearInterval(id);
        };
    }, [isOpen, campaignId, campaign?.status]);

    const scenarioLabelMap = useMemo(() => {
        const map = {};
        (scenarios || []).forEach(s => { map[String(s.value)] = s.label; });
        return map;
    }, [scenarios]);

    const getScenarioLabel = useCallback(
        (val) => scenarioLabelMap[String(val)] || String(val || ''),
        [scenarioLabelMap]
    );

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="!max-w-[900px] max-h-[80vh]" style={{ zIndex: 1000 }}>
                <DialogHeader>
                    <DialogTitle>{campaign?.name || 'Campaign Details'}</DialogTitle>
                    <DialogDescription>
                        Scenario: {campaign ? getScenarioLabel(campaign.scenario) : 'Loading...'}
                    </DialogDescription>
                </DialogHeader>

                {campaign ? (
                    <ScrollArea className="h-[500px] custom-scrollbar">
                        <div className="space-y-6">
                            {/* Campaign Overview */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <Card className="p-4">
                                    <div className="text-2xl font-bold">{campaign.total_contacts || 0}</div>
                                    <div className="text-sm text-muted-foreground">Total Contacts</div>
                                </Card>
                                <Card className="p-4">
                                    <div className="text-2xl font-bold text-green-600">{campaign.replied || 0}</div>
                                    <div className="text-sm text-muted-foreground">Replies</div>
                                </Card>
                                <Card className="p-4">
                                    <div className="text-2xl font-bold text-blue-600">{campaign.opened || 0}</div>
                                    <div className="text-sm text-muted-foreground">Opened</div>
                                </Card>
                                <Card className="p-4">
                                    <div className="text-2xl font-bold text-red-600">{campaign.bounced || 0}</div>
                                    <div className="text-sm text-muted-foreground">Bounced</div>
                                </Card>
                            </div>

                            {/* Template sended Info */}
                            <Card className="p-4">
                                <h4 className="font-medium mb-3">Sent Templates</h4>
                                {campaign.sent_templates?.length ? (
                                    campaign.sent_templates.map((tpl, idx) => (
                                        <div key={idx} className="mb-4 border-b pb-2">
                                            <div><strong>Subject:</strong> {tpl.subject}</div>
                                            <div><strong>Body:</strong> <div dangerouslySetInnerHTML={{ __html: tpl.body }} /></div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-muted-foreground text-sm">No templates sent</div>
                                )}
                            </Card>
                            {/* Template will be sent */}
                            <Card className="p-4">
                                <h4 className="font-medium mb-3">Emails Will Be Sent</h4>
                                {campaign.upcoming_templates?.length ? (
                                    campaign.upcoming_templates.map((tpl, idx) => (
                                        <div key={tpl.id} className="mb-2 border-b pb-2">
                                            <div><strong>Step {tpl.step_number}</strong></div>
                                            <div><strong>Title:</strong> {tpl.title}</div>
                                            <div><strong>Subject:</strong> {tpl.subject}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-muted-foreground text-sm">No templates selected for future emails</div>
                                )}
                            </Card>

                            {/* Contact Info */}
                            <Card className="p-4">
                                <h4 className="font-medium mb-3">Contacts</h4>
                                {campaign.contacts?.length ? (
                                    campaign.contacts.map(contact => (
                                        <div key={contact.id} className="border p-2 rounded mb-2">
                                            <div className="font-medium">{contact.name || 'No name'}</div>
                                            <div className="text-sm">{contact.organization_name || '—'}</div>
                                            <div className="text-sm text-muted-foreground">{contact.email || '—'}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-muted-foreground text-sm">No contacts selected</div>
                                )}
                            </Card>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <Button variant="outline" onClick={() => onEdit(campaign)}>
                                Edit
                            </Button>
                            <Button variant="destructive" onClick={() => onDelete(campaign.id)}>
                                Delete
                            </Button>
                        </div>
                    </ScrollArea>
                ) : (
                    <div className="p-4 text-muted-foreground">Loading or no data available.</div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CampaignDetailsModal;